import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react'
import { createPortal } from 'react-dom'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { AppModal } from '../../shared/components/AppModal'
import { useAppConfirm } from '../../shared/components/AppConfirmDialog'
import { useLocale } from '../../shared/i18n/LocaleContext'
import { PasswordInput } from '../../shared/components/PasswordInput'
import { PagedListControls } from '../../shared/components/PagedListControls'
import { PageLoading } from '../../shared/components/PageLoading'
import { listParams, SortableHeaders, useUrlSortedRows } from '../../shared/components/SortableTableHeader'
import { clearIntegrationProtectionCache } from '../../shared/integrations/useIntegrationProtection'
import { getPreferredPageSize, rememberPageSize } from '../../shared/pagination/pageSizePreference'
import { getIntegrationStudio, inspectContract, integrationCommand,
  integratorRoute, saveIntegration, testGet, getIntegrationImportCandidates,
  importIntegrations } from './integratorApi'
import type {
  IntegrationDefinition,
  IntegrationForm,
  IntegrationMapping,
  IntegrationOperation,
  IntegrationStudioSnapshot,
  IntegrationTargetEntityDescriptor,
  IntegrationTargetEntity,
  IntegrationImportCandidate,
} from './types'

const emptyForm = (): IntegrationForm => ({
  id: '', name: '', description: '', targetEntity: 'Material',
  serviceBaseUrl: '', apiDocumentationUrl: '', authenticationType: 'None',
  authenticationUserName: '', authenticationSecret: '',
  hasAuthenticationSecret: false, apiKeyName: '', apiKeyInQuery: false,
  operationPath: '', operationSummary: '', responseRootPath: '',
  contractSnapshotJson: '', scheduleInterval: 1, scheduleUnit: 'Hours',
  timeZoneId: 'Europe/Madrid', isScheduleEnabled: false, direction: 'Inbound',
  httpMethod: 'Get', externalCompanyField: '', externalCompanyValue: '',
  requestValues: [], paginationStrategy: 'None', pageParameter: 'page',
  pageSizeParameter: 'pageSize', pageSize: 100, firstPage: 1,
  offsetParameter: 'offset', keysetFieldPath: '', cursorParameter: 'cursor', nextCursorPath: '',
  nextLinkPath: '', totalCountPath: '', maximumPages: 10000,
  pageTimeoutSeconds: 120,
  trigger: 'Manual', triggerStatus: '', keyFields: [], mappings: [],
  panelConfigurations: [],
  protectsMappedFields: false,
})

function allowedValueLabel(field: string, value: string,
  t: (key: string) => string) {
  if (field === 'articleType') return t(`materials.type.${value}`)
  if (field === 'procurementType') return t(`materials.procurement.${value}`)
  return value
}

const counterTransformation = (fields: string[]) =>
  `Counter:${fields.join(',')}`
const ifNullTransformation = (fallbackPath: string) => `IfNull:${fallbackPath}`
const transformationKind = (value: string) =>
  value === 'Counter' || value.startsWith('Counter:') ? 'Counter'
    : value.startsWith('IfNull:') ? 'IfNull' : value
const transformationGeneratesValue = (value: string) =>
  transformationKind(value) === 'Counter'
const counterGroupFields = (value: string) => value.startsWith('Counter:')
  ? value.slice('Counter:'.length).split(',').map(field => field.trim()).filter(Boolean)
  : []
const ifNullFallbackPath = (value: string) => value.startsWith('IfNull:')
  ? value.slice('IfNull:'.length).trim() : ''
const defaultFunctionPrefix = '@Function:'
const defaultValueKind = (value: string) => value.startsWith(defaultFunctionPrefix)
  ? 'Function' : 'Fixed'
const defaultFunction = (value: string) => value.startsWith(defaultFunctionPrefix)
  ? value.slice(defaultFunctionPrefix.length) : 'UtcNow'
const outboundFunctionsEnabled = (form: IntegrationForm) =>
  form.direction === 'Outbound' && ['Post', 'Put', 'Patch'].includes(form.httpMethod)
const outboundDefaultFunctions = ['UtcNow', 'LocalNow', 'UtcToday', 'LocalToday',
  'Uuid', 'ExternalCompany', 'LastSuccessfulRunUtc'] as const
const defaultPanelConfigurations = (target?: IntegrationTargetEntityDescriptor) =>
  (target?.panels ?? []).map(panel => ({ panelKey: panel.key,
    responseRootPath: '', keyFields:
      panel.fields.filter(field => field.required).map(field => field.key) }))
const defaultKeyFields = (target?: IntegrationTargetEntityDescriptor) =>
  target?.isGrouped ? defaultPanelConfigurations(target).flatMap(panel =>
    panel.keyFields.map(field => `${panel.panelKey}.${field}`)) :
    target?.fields.filter(field => field.required).slice(0, 1).map(field => field.key) ?? []

const usesCompositeKeys = (operationPath: string) => /\{keys\}/i.test(operationPath)
const automaticKeysTemplate = (form: IntegrationForm,
  target?: IntegrationTargetEntityDescriptor) => {
  const parts: string[] = []
  if (form.externalCompanyField.trim() && form.externalCompanyValue.trim())
    parts.push(`${form.externalCompanyField.trim()}={company}`)
  const rootPrefix = target?.panels?.find(panel => panel.isRoot)?.key
  const identity = form.mappings.filter(mapping => mapping.sourcePath.trim() &&
    mapping.targetField.trim() && (!rootPrefix ||
      mapping.targetField.startsWith(`${rootPrefix}.`))).sort((left, right) => {
        const rank = (field: string) => {
          const name = field.split('.').at(-1)?.toLocaleLowerCase()
          return name === 'number' ? 0 : name === 'code' ? 1 : name === 'id' ? 2 : 3
        }
        return rank(left.targetField) - rank(right.targetField)
      }).find(mapping => ['number', 'code', 'id'].includes(
        mapping.targetField.split('.').at(-1)?.toLocaleLowerCase() ?? ''))
  if (identity) parts.push(`${identity.sourcePath.trim()}={${identity.targetField}}`)
  return parts.join(',')
}

type Translator = ReturnType<typeof useLocale>['t']
const targetFieldLabel = (field: string, t: Translator) => {
  const plainField = field.split('.').at(-1) ?? field
  const labels: Partial<Record<string, Parameters<Translator>[0]>> = {
    code: 'aplPanel.code', name: 'aplPanel.name', description: 'administration.description',
    routingCode: 'engineering.routing', routingVersion: 'engineering.version',
    sequence: 'engineering.phase', operationCode: 'engineering.operation',
    workCenterCode: 'engineering.workCenter',
    machineSetupMinutes: 'engineering.machineSetupMinutes',
    laborSetupMinutes: 'engineering.laborSetupMinutes',
    machineRunMinutesPerUnit: 'engineering.machineRunMinutes',
    laborRunMinutesPerUnit: 'engineering.laborRunMinutes',
    queueMinutes: 'engineering.queueMinutes', waitMinutes: 'engineering.waitMinutes',
    transportMinutes: 'engineering.transportMinutes', cleanupMinutes: 'engineering.cleanupMinutes',
    transferBatchQuantity: 'engineering.transferBatch',
    isSubcontracted: 'engineering.subcontracted', instructions: 'engineering.instructions',
  }
  const key = labels[plainField]
  return key ? t(key) : plainField.replace(/([a-z0-9])([A-Z])/g, '$1 $2')
}

const withoutManualFieldsQuery = (value:string) => {
  try {
    const url = new URL(value)
    for (const key of [...url.searchParams.keys()])
      if (key.toLocaleLowerCase() === 'fields') url.searchParams.delete(key)
    return url.toString()
  } catch { return value }
}

export function IntegrationStudioPage() {
  const { configurationId = '', companyId = '' } = useParams()
  const context = useMemo(() => ({ configurationId, companyId }),
    [configurationId, companyId])
  const studioRoute = integratorRoute(context)
  const { t, formatDateTime, formatNumber, locale } = useLocale()
  const [searchParams, setSearchParams] = useSearchParams()
  const page = Math.max(1, Number(searchParams.get('page') ?? 1) || 1)
  const pageSize = getPreferredPageSize(searchParams)
  const appliedQuery = searchParams.get('q') ?? ''
  const appliedStatus = searchParams.get('status') ?? ''
  const appliedEntity = searchParams.get('entity') ?? ''
  const [query, setQuery] = useState(appliedQuery)
  const [status, setStatus] = useState(appliedStatus)
  const [entity, setEntity] = useState(appliedEntity)
  useEffect(()=>{setQuery(appliedQuery);setStatus(appliedStatus);setEntity(appliedEntity)},
    [appliedQuery,appliedStatus,appliedEntity])
  const { confirm, confirmationDialog } = useAppConfirm()
  const [snapshot, setSnapshot] = useState<IntegrationStudioSnapshot | null>(null)
  const [form, setForm] = useState<IntegrationForm>(emptyForm)
  const [operations, setOperations] = useState<IntegrationOperation[]>([])
  const [modalOpen, setModalOpen] = useState(false)
  const [step, setStep] = useState(1)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const [testResult, setTestResult] = useState<Awaited<ReturnType<typeof testGet>>|null>(null)
  const [importOpen, setImportOpen] = useState(false)
  const [importSourceId, setImportSourceId] = useState('')
  const [importCandidates, setImportCandidates] = useState<IntegrationImportCandidate[]>([])
  const [selectedImports, setSelectedImports] = useState<string[]>([])
  const [outboundFieldToAdd, setOutboundFieldToAdd] = useState('')

  const load = useCallback(async () => {
    try {
      setSnapshot(await getIntegrationStudio(context, {query:appliedQuery,status:appliedStatus,
        entity:appliedEntity,page,pageSize}))
      setError(null)
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'integrator.loadError')
    }
  }, [appliedQuery, appliedStatus, appliedEntity, context, page, pageSize])

  useEffect(() => { void load() }, [load])

  useEffect(() => {
    if (modalOpen) return
    const refresh = window.setInterval(() => {
      if (document.visibilityState === 'visible') void load()
    }, 15_000)
    return () => window.clearInterval(refresh)
  }, [load, modalOpen])

  const target = useMemo(() => snapshot?.targetEntities.find(
    item => item.key === form.targetEntity), [form.targetEntity, snapshot])

  const selectedOperation = operations.find(item => item.path === form.operationPath &&
    item.method === form.httpMethod.toUpperCase())
  const isOutboundMapping = form.direction === 'Outbound' &&
    ['Post', 'Put', 'Patch'].includes(form.httpMethod)
  const isRequiredOutboundField = (required: boolean) =>
    required && form.httpMethod !== 'Patch'
  const mappingRows = useMemo(() => {
    if (isOutboundMapping) {
      const selectedPaths = new Set(form.mappings.map(mapping => mapping.sourcePath))
      return (selectedOperation?.fields ?? []).filter(external =>
        isRequiredOutboundField(external.required) ||
        selectedPaths.has(external.path)).map(external => {
          const mapping = form.mappings.find(item => item.sourcePath === external.path) ?? {
            sourcePath: external.path, targetField: '', defaultValue: '', transformation: 'None',
            unknownValueBehavior: 'Reject' as const, valueMappings: [],
          }
          return { external, field: target?.fields.find(item =>
            item.key === mapping.targetField), mapping, rowKey: external.path }
        })
    }
    return target?.fields.map(field => ({ external: undefined, field,
      mapping: form.mappings.find(mapping => mapping.targetField === field.key) ?? {
        sourcePath: '', targetField: field.key, defaultValue: '', transformation: 'None',
        unknownValueBehavior: 'Reject' as const, valueMappings: [],
      }, rowKey: field.key })) ?? []
  }, [form.httpMethod, form.mappings, isOutboundMapping, selectedOperation, target])
  const availableOutboundFields = useMemo(() => (selectedOperation?.fields ?? [])
    .filter(field => !isRequiredOutboundField(field.required) &&
      !form.mappings.some(mapping => mapping.sourcePath === field.path)),
    [form.httpMethod, form.mappings, selectedOperation])
  const outboundMappingsComplete = !isOutboundMapping ||
    (form.mappings.some(mapping => mapping.targetField) &&
    (selectedOperation?.fields.filter(field =>
      isRequiredOutboundField(field.required)).every(field =>
      form.mappings.some(mapping => mapping.sourcePath === field.path &&
        mapping.targetField)) ?? false))
  const automaticFields = useMemo(() => [...new Set([...form.mappings
    .map(mapping => mapping.sourcePath.trim().split('.').filter(Boolean).at(-1) ?? ''),
    ...(form.paginationStrategy === 'Keyset' ?
      [form.keysetFieldPath.trim().split('.').filter(Boolean).at(-1) ?? ''] : [])]
    .filter(Boolean))].join(','),
    [form.mappings, form.paginationStrategy, form.keysetFieldPath])
  const configuredKeys = form.requestValues.find(value => value.location === 'Path' &&
    value.name.toLocaleLowerCase() === 'keys')?.value ?? ''
  const effectiveKeys = configuredKeys || automaticKeysTemplate(form, target)
  const updateKeysTemplate = (value: string) => setForm(current => ({ ...current,
    requestValues: [...current.requestValues.filter(item => !(item.location === 'Path' &&
      item.name.toLocaleLowerCase() === 'keys')), ...(value.trim() ? [{ location: 'Path' as const,
        name: 'keys', value, valueSource: 'Constant' as const }] : [])] }))

  const selectImportSource = async (sourceCompanyId:string) => {
    setImportSourceId(sourceCompanyId); setSelectedImports([]); setImportCandidates([])
    if (!sourceCompanyId) return
    setBusy(true); setError(null)
    try {
      const candidates = await getIntegrationImportCandidates(context, sourceCompanyId)
      setImportCandidates(candidates); setSelectedImports(candidates.map(x=>x.id))
    } catch (reason) { setError(reason instanceof Error ? reason.message : 'integrator.importError') }
    finally { setBusy(false) }
  }

  const runImport = async () => {
    if (!importSourceId || !selectedImports.length) return
    if (!await confirm(t('integrator.importConfirm'))) return
    setBusy(true); setError(null)
    try {
      const result = await importIntegrations(context, importSourceId, selectedImports)
      clearIntegrationProtectionCache()
      setMessage(result.skippedCount ? 'integrator.importedWithSkipped' : result.code)
      setImportOpen(false); setImportSourceId(''); setImportCandidates([]); setSelectedImports([])
      await load()
    } catch (reason) { setError(reason instanceof Error ? reason.message : 'integrator.importError') }
    finally { setBusy(false) }
  }

  const closeImport = useCallback(() => {
    setImportOpen(false)
    setImportSourceId('')
    setImportCandidates([])
    setSelectedImports([])
  }, [])

  useEffect(() => {
    if (!importOpen) return
    const previousOverflow = document.body.style.overflow
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeImport()
    }
    document.body.style.overflow = 'hidden'
    document.addEventListener('keydown', closeOnEscape)
    return () => {
      document.body.style.overflow = previousOverflow
      document.removeEventListener('keydown', closeOnEscape)
    }
  }, [closeImport, importOpen])

  const openNew = () => {
    const fresh = emptyForm()
    const first = snapshot?.targetEntities[0]
    if (first) { fresh.targetEntity = first.key;
      fresh.keyFields = defaultKeyFields(first)
      fresh.panelConfigurations = defaultPanelConfigurations(first) }
    setForm(fresh)
    setOperations([])
    setStep(1)
    setError(null)
    setTestResult(null)
    setModalOpen(true)
    if (searchParams.get('new') !== '1') setSearchParams({ ...Object.fromEntries(searchParams), new: '1' })
  }

  const openEdit = (item: IntegrationDefinition) => {
    const defaultKeys = snapshot?.targetEntities.find(x => x.key === item.targetEntity)
      ?.fields.filter(x => x.required).slice(0, 1).map(x => x.key) ?? []
    setForm({
      id: item.id, name: item.name, description: item.description,
      targetEntity: item.targetEntity,
      serviceBaseUrl: withoutManualFieldsQuery(item.serviceBaseUrl),
      apiDocumentationUrl: item.apiDocumentationUrl,
      authenticationType: item.authenticationType,
      authenticationUserName: item.authenticationUserName,
      authenticationSecret: '', hasAuthenticationSecret: item.hasAuthenticationSecret,
      apiKeyName: item.apiKeyName, apiKeyInQuery: item.apiKeyInQuery,
      operationPath: item.operationPath, operationSummary: item.operationSummary,
      responseRootPath: item.responseRootPath,
      contractSnapshotJson: '', scheduleInterval: item.scheduleInterval,
      scheduleUnit: item.scheduleUnit, timeZoneId: item.timeZoneId,
      isScheduleEnabled: item.isScheduleEnabled, direction: item.direction,
      httpMethod: item.httpMethod, externalCompanyField: item.externalCompanyField,
      externalCompanyValue: item.externalCompanyValue,
      requestValues: item.requestValues.filter(value => value.location !== 'Query' ||
        value.name.toLocaleLowerCase() !== 'fields'),
      paginationStrategy: item.paginationStrategy,
      pageParameter: item.pageParameter, pageSizeParameter: item.pageSizeParameter,
      pageSize: item.pageSize, firstPage: item.firstPage,
      offsetParameter: item.offsetParameter, keysetFieldPath: item.keysetFieldPath,
      cursorParameter: item.cursorParameter,
      nextCursorPath: item.nextCursorPath, nextLinkPath: item.nextLinkPath,
      totalCountPath: item.totalCountPath, maximumPages: item.maximumPages,
      pageTimeoutSeconds: item.pageTimeoutSeconds,
      trigger: item.trigger, triggerStatus: item.triggerStatus,
      protectsMappedFields: item.protectsMappedFields,
      keyFields: item.keyFields.length ? item.keyFields : defaultKeys,
      mappings: item.mappings.map(mapping => ({ ...mapping,
        targetField: mapping.targetField.startsWith('$outbound:')
          ? '' : mapping.targetField })),
      panelConfigurations: item.panelConfigurations ?? [],
    })
    setOperations([])
    setStep(1)
    setError(null)
    setTestResult(null)
    setModalOpen(true)
    if (searchParams.get('edit') !== item.id) setSearchParams({ ...Object.fromEntries(searchParams), edit: item.id })
  }

  useEffect(() => {
    if (!snapshot) return
    if (searchParams.get('new') === '1') { openNew(); return }
    const id = searchParams.get('edit')
    const item = snapshot.integrations.find(candidate => candidate.id === id)
    if (item) openEdit(item)
    else if (!id) setModalOpen(false)
  }, [modalOpen, searchParams, snapshot])

  const closeEditor = () => {
    setModalOpen(false)
    const next = new URLSearchParams(searchParams); next.delete('edit'); next.delete('new')
    setSearchParams(next, { replace: true })
  }

  const discover = async () => {
    setBusy(true)
    setError(null)
    try {
      const inspection = await inspectContract(context, form)
      setOperations(inspection.operations)
      const allowedMethod = form.httpMethod.toUpperCase()
      const selected = inspection.operations.find(operation =>
        operation.path === form.operationPath && operation.method === allowedMethod) ??
        inspection.operations.find(operation => operation.method === allowedMethod)
      setForm(current => ({
        ...current,
        contractSnapshotJson: inspection.contractSnapshotJson,
        operationPath: selected?.path ?? '',
        operationSummary: selected?.summary ?? '',
        responseRootPath: selected?.path === current.operationPath &&
          selected.method === current.httpMethod.toUpperCase()
          ? current.responseRootPath || selected.responseRootPath
          : selected?.responseRootPath ?? '',
      }))
      setStep(3)
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'integrator.inspectError')
    } finally { setBusy(false) }
  }

  const selectOperation = (value: string) => {
    const [method, ...pathParts] = value.split(' ')
    const path = pathParts.join(' ')
    const operation = operations.find(item => item.path === path && item.method === method)
    if (!operation) return
    const currentTarget = snapshot?.targetEntities.find(
      item => item.key === form.targetEntity)
    const inboundSuggestions: IntegrationMapping[] = (currentTarget?.fields ?? []).map(field => {
      const external = operation.fields.find(candidate =>
        candidate.path.split('.').at(-1)?.toLocaleLowerCase() ===
          field.key.toLocaleLowerCase())
      return {
        sourcePath: external?.path ?? '', targetField: field.key,
        defaultValue: '', transformation: 'None', unknownValueBehavior: 'Reject',
        valueMappings: [],
      }
    })
    const outboundSuggestions: IntegrationMapping[] = operation.fields
      .filter(external => external.required && operation.method !== 'PATCH')
      .map(external => {
        const aplField = currentTarget?.fields.find(field =>
          external.path.split('.').at(-1)?.toLocaleLowerCase() ===
            field.key.toLocaleLowerCase())
        return { sourcePath: external.path, targetField: aplField?.key ?? '',
          defaultValue: '', transformation: 'None', unknownValueBehavior: 'Reject',
          valueMappings: [] }
      })
    setForm(current => ({ ...current, operationPath: operation.path,
      httpMethod: (operation.method[0] + operation.method.slice(1).toLowerCase()) as IntegrationForm['httpMethod'],
      operationSummary: operation.summary,
      responseRootPath: operation.responseRootPath,
      mappings: operation.method === 'GET' ? inboundSuggestions : outboundSuggestions }))
    setOutboundFieldToAdd('')
    setTestResult(null)
  }

  const updateMapping = (targetField: string, patch: Partial<IntegrationMapping>) => {
    setForm(current => {
      const existing = current.mappings.find(item => item.targetField === targetField)
      const next = { sourcePath: '', targetField, defaultValue: '',
        transformation: 'None', unknownValueBehavior: 'Reject' as const,
        valueMappings: [], ...existing, ...patch }
      return { ...current, mappings: [
        ...current.mappings.filter(item => item.targetField !== targetField), next,
      ] }
    })
  }

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    setBusy(true)
    setError(null)
    try {
      const allowedFields = new Set(target?.fields.map(field => field.key) ?? [])
      const effectiveRequestValues = form.requestValues.filter(value =>
        !(value.location === 'Path' && value.name.toLocaleLowerCase() === 'keys'))
      if (usesCompositeKeys(form.operationPath) && effectiveKeys.trim())
        effectiveRequestValues.push({ location: 'Path', name: 'keys',
          value: effectiveKeys, valueSource: 'Constant' })
      const payload = { ...form,
        protectsMappedFields: form.direction === 'Inbound' &&
          form.httpMethod === 'Get' && form.protectsMappedFields,
        requestValues: effectiveRequestValues.filter(value => value.location !== 'Query' ||
          value.name.toLocaleLowerCase() !== 'fields'),
        keyFields: form.keyFields.filter(field => allowedFields.has(field)),
        mappings: form.mappings.filter(mapping =>
          (allowedFields.has(mapping.targetField) || form.direction === 'Outbound' &&
            !mapping.targetField.trim() && (mapping.defaultValue.trim() ||
              transformationGeneratesValue(mapping.transformation))) &&
          (mapping.sourcePath.trim() || mapping.defaultValue.trim() ||
            transformationGeneratesValue(mapping.transformation))).map(mapping => ({
              ...mapping,
              valueMappings: Array.from(mapping.valueMappings
                .filter(value => value.externalValue.trim())
                .map(value => ({ ...value, externalValue: value.externalValue.trim(),
                  targetValue: value.targetValue.trim() }))
                .reduce((values, value) => {
                  const key = value.externalValue.toLocaleUpperCase()
                  const current = values.get(key)
                  if (!current || value.targetValue) values.set(key, value)
                  return values
                }, new Map<string, typeof mapping.valueMappings[number]>()).values()),
            })) }
      const code = await saveIntegration(context, payload)
      clearIntegrationProtectionCache()
      setMessage(code)
      closeEditor()
      await load()
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'integrator.saveError')
    } finally { setBusy(false) }
  }
  const runTest = async (all: boolean) => {
    if (all && !await confirm(t('integrator.testAllPagesConfirm'))) return
    setBusy(true); setError(null)
    try { setTestResult(await testGet(context, form, all)); if (form.id) await load() }
    catch (reason) { setError(reason instanceof Error ? reason.message : 'integrator.testFailed') }
    finally { setBusy(false) } }
  const runCommand = async (item: IntegrationDefinition,
    action: 'activate'|'pause'|'archive'|'restore'|'execute') => {
    const confirmationKey = action === 'execute' ? 'integrator.executeConfirm' :
      action === 'archive' ? 'integrator.archiveConfirm' : null
    if (confirmationKey && !await confirm(t(confirmationKey))) return
    setBusy(true); setError(null)
    try { const code = await integrationCommand(context, item.id, action); clearIntegrationProtectionCache(); setMessage(code); await load() }
    catch (reason) { setError(reason instanceof Error ? reason.message : 'integrator.commandFailed') }
    finally { setBusy(false) }
  }

  const updateOutboundMapping = (sourcePath: string,
    patch: Partial<IntegrationMapping>) => {
    setForm(current => {
      const existing = current.mappings.find(item => item.sourcePath === sourcePath)
      const next = { sourcePath, targetField: '', defaultValue: '',
        transformation: 'None', unknownValueBehavior: 'Reject' as const,
        valueMappings: [], ...existing, ...patch }
      return { ...current, mappings: [
        ...current.mappings.filter(item => item.sourcePath !== sourcePath), next,
      ] }
    })
  }

  const updateRowMapping = (rowKey: string, patch: Partial<IntegrationMapping>) =>
    isOutboundMapping ? updateOutboundMapping(rowKey, patch) : updateMapping(rowKey, patch)

  const addOutboundField = () => {
    if (!outboundFieldToAdd) return
    updateOutboundMapping(outboundFieldToAdd, {})
    setOutboundFieldToAdd('')
  }

  const removeOutboundField = (sourcePath: string) => setForm(current => ({
    ...current, mappings: current.mappings.filter(mapping =>
      mapping.sourcePath !== sourcePath),
  }))
  const tableSort=useUrlSortedRows(snapshot?.integrations??[],{name:x=>x.name,entity:x=>x.targetEntity,
    endpoint:x=>`${x.httpMethod} ${x.operationPath}`,schedule:x=>x.isScheduleEnabled?x.scheduleInterval:null,status:x=>x.status},'name','asc',locale)
  if (!snapshot) return <PageLoading label={t('integrator.loading')}
    error={error ? t(error as Parameters<typeof t>[0]) : null}/>

  return <main className="page-content">
    {confirmationDialog}
    <header className="welcome administration-heading">
      <div><span className="eyebrow">{t('integrator.eyebrow')}</span>
        <h1>{t('integrator.title')}</h1>
        <p>{snapshot.companyName} · {t('integrator.subtitle')}</p></div>
      <div className="table-actions">
        <button className="primary-button" type="button" onClick={openNew}>
          + {t('integrator.new')}</button>
      </div>
    </header>
    <nav className="apl-context-navigation"
      aria-label={t('recordNavigation.backToList')}>
      <Link className="secondary-button" to={`/apl/configuraciones/${configurationId}`}>
        ← {t('administration.companies')}
      </Link>
    </nav>
    {error && !modalOpen && !importOpen &&
      <p className="form-error">{t(error as Parameters<typeof t>[0])}</p>}
    {message && <p className="form-success">{t(message as Parameters<typeof t>[0])}</p>}
    {importOpen && createPortal(<div className="modal-backdrop integrator-import-backdrop"
      onMouseDown={event=>{if(event.target===event.currentTarget) closeImport()}}>
      <section className="administration-modal wide integrator-import-modal" role="dialog"
        aria-modal="true" aria-labelledby="integrator-import-title"
        onMouseDown={event=>event.stopPropagation()}>
        <header><h2 id="integrator-import-title">{t('integrator.importFromCompany')}</h2>
          <button className="modal-close" type="button" aria-label={t('nav.closeMenu')}
            onClick={closeImport}>×</button></header>
        <div className="administration-modal-body integrator-import-panel">
          <p className="form-help">{t('integrator.importHelp')}</p>
          {error && <p className="modal-feedback">{t(error as Parameters<typeof t>[0])}</p>}
          <div className="administration-form"><label><span>{t('integrator.sourceCompany')}</span>
            <select autoFocus value={importSourceId}
              onChange={event=>void selectImportSource(event.target.value)}>
              <option value="">—</option>{snapshot.importSources.map(source=><option key={source.id}
                value={source.id}>{source.code} · {source.name} ({source.integrationCount})</option>)}</select>
          </label></div>
          {!snapshot.importSources.length &&
            <p className="empty-state">{t('integrator.importSourceUnavailable')}</p>}
          {importSourceId && <><div className="table-actions integrator-import-actions">
            <button type="button" className="table-action" onClick={()=>setSelectedImports(
              importCandidates.map(x=>x.id))}>{t('integrator.selectAll')}</button>
            <button type="button" className="table-action" onClick={()=>setSelectedImports([])}>
              {t('integrator.selectNone')}</button></div>
            <div className="administration-table-wrap"><table className="data-table administration-table">
              <thead><tr><th>{t('integrator.importSelection')}</th><th>{t('integrator.name')}</th>
                <th>{t('integrator.entity')}</th><th>{t('planning.status')}</th></tr></thead>
              <tbody>{importCandidates.map(item=><tr key={item.id}><td><input type="checkbox"
                checked={selectedImports.includes(item.id)} onChange={event=>setSelectedImports(current=>
                  event.target.checked ? [...current,item.id] : current.filter(id=>id!==item.id))}/></td>
                <td>{item.name}</td><td>{t(`integrator.entity.${item.targetEntity}`)}</td>
                <td>{t(`integrator.status.${item.status}`)}</td></tr>)}</tbody></table></div>
            {!busy && !importCandidates.length && <p className="empty-state">{t('integrator.importEmpty')}</p>}
            <div className="form-actions"><button className="secondary-button" type="button"
              onClick={closeImport}>{t('administration.cancel')}</button>
              <button className="primary-button" type="button"
                disabled={busy || !selectedImports.length} onClick={()=>void runImport()}>
                {t('integrator.importSelected')}</button></div></>}
        </div>
      </section>
    </div>, document.body)}
    <section className="integrator-summary">
      <article><span>{t('integrator.total')}</span><strong>{formatNumber(snapshot.totalDefinitions)}</strong></article>
      <article><span>{t('integrator.configuredSchedules')}</span><strong>{formatNumber(snapshot.scheduledCount)}</strong></article>
      <article><span>{t('integrator.status.Active')}</span><strong>{formatNumber(snapshot.activeCount)}</strong></article>
      <Link className="integrator-summary-link" to={`${studioRoute}/conexiones`}><span>{t('integrator.runningConnections')}</span><strong>{formatNumber(snapshot.runningCount)}</strong></Link>
    </section>
    <section className="data-panel administration-panel">
      <div className="section-heading"><div><h2>{t('integrator.list')}</h2>
        <p>{t('integrator.listHelp')}</p></div>
        <div className="table-actions">
          <button className="secondary-button" type="button"
            onClick={()=>setImportOpen(true)}>{t('integrator.importFromCompany')}</button>
          <button className="primary-button" type="button" onClick={openNew}>
            + {t('integrator.new')}</button>
        </div>
      </div>
      <PagedListControls applyLabel={t('recordList.applyFilters')} clearLabel={t('recordList.clearFilters')}
        rowsPerPageLabel={t('recordList.rowsPerPage')} previousLabel={t('recordList.previousPage')}
        nextLabel={t('recordList.nextPage')} pageLabel={t('recordList.pageOf')} totalLabel={t('recordList.totalRecords')}
        page={page} pageSize={pageSize} totalCount={snapshot.totalCount} formatNumber={formatNumber}
        filters={<><label><span>{t('nav.search')}</span><input value={query} placeholder={t('recordList.search')}
          onChange={event=>setQuery(event.target.value)}/></label><label><span>{t('planning.status')}</span>
          <select value={status} onChange={event=>setStatus(event.target.value)}><option value="">{t('recordList.allStatuses')}</option>
            {['Draft','Active','Paused','Archived'].map(value=><option value={value} key={value}>{t(`integrator.status.${value}`)}</option>)}</select></label>
          <label><span>{t('integrator.entity')}</span><select value={entity} onChange={event=>setEntity(event.target.value)}>
            <option value="">—</option>{snapshot.targetEntities.map(item=><option key={item.key} value={item.key}>{t(`integrator.entity.${item.key}`)}</option>)}</select></label></>}
        onApply={()=>setSearchParams(listParams(searchParams,{q:query.trim()||undefined,status:status||undefined,entity:entity||undefined,page:'1',pageSize:String(pageSize)}))}
        onClear={()=>{setQuery('');setStatus('');setEntity('');setSearchParams(listParams(searchParams,{page:'1',pageSize:String(pageSize)}))}}
        onPageChange={next=>setSearchParams(listParams(searchParams,{q:appliedQuery||undefined,status:appliedStatus||undefined,entity:appliedEntity||undefined,page:String(next),pageSize:String(pageSize)}))}
        onPageSizeChange={next=>{rememberPageSize(next);setSearchParams(listParams(searchParams,{q:appliedQuery||undefined,status:appliedStatus||undefined,entity:appliedEntity||undefined,page:'1',pageSize:String(next)}))}}/>
      <div className="administration-table-wrap"><table className="data-table administration-table">
        <thead><tr><SortableHeaders sort={tableSort} columns={[["name",t('integrator.name')],["entity",t('integrator.entity')],["endpoint",t('integrator.endpoint')],["schedule",t('integrator.schedule')],["status",t('planning.status')]]}/><th>{t('aplPanel.actions')}</th></tr></thead>
        <tbody>{tableSort.sortedRows.map(item => <tr key={item.id}>
          <td><Link className="record-code-link" to={`?${new URLSearchParams({ ...Object.fromEntries(searchParams), edit: item.id }).toString()}`}
            state={{recordIds:tableSort.sortedRows.map(value=>value.id)}}>{item.name}</Link><small>{item.description || '—'}</small></td>
          <td>{t(`integrator.entity.${item.targetEntity}`)}</td>
          <td><code>{item.httpMethod.toUpperCase()} {item.operationPath}</code></td>
          <td>{item.isScheduleEnabled
            ? `${t('integrator.every')} ${item.scheduleInterval} ${t(`integrator.unit.${item.scheduleUnit}`)}`
            : t('integrator.manual')}</td>
          <td><span className={`status-pill ${item.status === 'Active' ? 'success' :
            item.status === 'Archived' ? 'danger' : 'warning'}`}>{t(`integrator.status.${item.status}`)}</span></td>
          <td><div className="table-actions">
            <Link className="table-action" to={`${studioRoute}/${item.id}/conexiones`}>
              {t('integrator.viewConnections')}</Link>
            {item.status === 'Active' && <button className="table-action" type="button"
              disabled={busy} onClick={() => void runCommand(item, 'execute')}>
              {t('integrator.executeNow')}</button>}
            {item.status === 'Draft' && <button className="table-action" type="button"
              onClick={() => void runCommand(item, 'activate')}>{t('integrator.activate')}</button>}
            {item.status === 'Active' && <button className="table-action" type="button"
              onClick={() => void runCommand(item, 'pause')}>{t('integrator.pause')}</button>}
            {item.status === 'Paused' && <button className="table-action" type="button"
              onClick={() => void runCommand(item, 'activate')}>{t('integrator.reactivate')}</button>}
            {item.status !== 'Archived' && <button className="table-action danger" type="button"
              onClick={() => void runCommand(item, 'archive')}>{t('integrator.archive')}</button>}
            {item.status === 'Archived' && <button className="table-action" type="button"
              onClick={() => void runCommand(item, 'restore')}>{t('integrator.restore')}</button>}
          </div></td>
        </tr>)}</tbody></table></div>
      {snapshot.integrations.length === 0 && <p className="empty-state">{t('integrator.empty')}</p>}
    </section>
    {modalOpen && <AppModal title={form.id ? t('integrator.edit') : t('integrator.new')}
      closeLabel={t('nav.closeMenu')} onClose={closeEditor} wide
      saveForm={step === 6} saveDisabled={busy}
      extraClassName="integrator-modal" feedback={error ? t(error as Parameters<typeof t>[0]) : null}>
      <form key={form.id || 'new'} className="integrator-wizard administration-form" onSubmit={submit}>
        <ol className="wizard-steps">{[1, 2, 3, 4, 5, 6].map(number => <li
          className={number === step ? 'active' : number < step ? 'done' : ''}
          key={number}><span>{number}</span>{t(`integrator.step${number}`)}</li>)}</ol>

        {step === 1 && <section className="wizard-panel">
          <h3>{t('integrator.step1Title')}</h3>
          <div className="compact-form-grid">
            <label><span>{t('integrator.name')}</span><input required maxLength={200}
              value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}/></label>
            <label><span>{t('integrator.entity')}</span><select value={form.targetEntity}
              onChange={e => { const entity = snapshot.targetEntities.find(x =>
                x.key === e.target.value); setForm({ ...form,
                targetEntity: e.target.value as IntegrationTargetEntity, mappings: [],
                keyFields: defaultKeyFields(entity),
                panelConfigurations: defaultPanelConfigurations(entity) }) }}>{snapshot.targetEntities.map(item => <option value={item.key}
                  key={item.key}>{t(`integrator.entity.${item.key}`)}</option>)}</select></label>
            <label><span>{t('integrator.direction')}</span><select value={form.direction}
              onChange={e => setForm({ ...form,
                direction: e.target.value as IntegrationForm['direction'],
                httpMethod: e.target.value === 'Inbound' ? 'Get' : 'Post',
                mappings: e.target.value === 'Inbound' ? form.mappings.map(mapping => ({
                  ...mapping,
                  defaultValue: defaultValueKind(mapping.defaultValue) === 'Function'
                    ? '' : mapping.defaultValue,
                })) : form.mappings,
                protectsMappedFields: e.target.value === 'Inbound'
                  ? form.protectsMappedFields : false })}>
              <option value="Inbound">{t('integrator.direction.Inbound')}</option>
              <option value="Outbound">{t('integrator.direction.Outbound')}</option>
            </select></label>
            <label><span>{t('integrator.targetCompany')}</span>
              <input disabled value={snapshot.companyName}/></label>
          </div>
          <label><span>{t('integrator.description')}</span><textarea maxLength={1000}
            value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}/></label>
          {form.direction === 'Inbound' && <><label className="checkbox-row"><input
            type="checkbox" checked={form.protectsMappedFields}
            onChange={e => setForm({ ...form, protectsMappedFields: e.target.checked })}/>
            <span>{t('integrator.protectsMappedFields')}</span></label>
          <p className="form-help">{t('integrator.protectsMappedFieldsHelp')}</p></>}
        </section>}

        {step === 2 && <section className="wizard-panel">
          <h3>{t('integrator.step2Title')}</h3>
          <div className="compact-form-grid">
            <label><span>{t('integrator.baseUrl')}</span><input required type="url"
              placeholder="https://api.example.com" value={form.serviceBaseUrl}
              onChange={e => setForm({ ...form, serviceBaseUrl: e.target.value })}
              onBlur={e => setForm(current => ({ ...current,
                serviceBaseUrl: withoutManualFieldsQuery(e.target.value) }))}/></label>
            <label><span>{t('integrator.apiDocUrl')}</span><input required type="url"
              placeholder="https://api.example.com/swagger" value={form.apiDocumentationUrl}
              onChange={e => setForm({ ...form, apiDocumentationUrl: e.target.value })}/></label>
            <label><span>{t('integrator.authentication')}</span><select
              value={form.authenticationType} onChange={e => setForm({ ...form,
                authenticationType: e.target.value as IntegrationForm['authenticationType'] })}>
              {(['None','Basic','Bearer','ApiKey'] as const).map(value => <option value={value}
                key={value}>{t(`integrator.auth.${value}`)}</option>)}</select></label>
            {form.authenticationType === 'Basic' && <label><span>{t('integrator.userName')}</span>
              <input required value={form.authenticationUserName}
                onChange={e => setForm({ ...form, authenticationUserName: e.target.value })}/></label>}
            {form.authenticationType === 'ApiKey' && <label><span>{t('integrator.apiKeyName')}</span>
              <input required value={form.apiKeyName}
                onChange={e => setForm({ ...form, apiKeyName: e.target.value })}/></label>}
            {form.authenticationType !== 'None' && <label><span>{t('integrator.secret')}</span>
              <PasswordInput required={!form.hasAuthenticationSecret}
                placeholder={form.hasAuthenticationSecret ? t('integrator.secretStored') : ''}
                value={form.authenticationSecret}
                onChange={e => setForm({ ...form, authenticationSecret: e.target.value })}/></label>}
          </div>
          <p className="form-help">{t('integrator.httpsHelp')}</p>
        </section>}

        {step === 3 && <section className="wizard-panel">
          <h3>{t('integrator.step3Title')}</h3>
          <label><span>{t('integrator.httpMethod')}</span><select value={form.httpMethod}
            onChange={e => setForm({ ...form, httpMethod:
              e.target.value as IntegrationForm['httpMethod'], operationPath: '' })}>
            {(form.direction === 'Inbound' ? ['Get'] : ['Post','Put','Patch']).map(value =>
              <option value={value} key={value}>{value.toUpperCase()}</option>)}</select></label>
          <label><span>{t('integrator.operation')}</span><select required
            value={selectedOperation ? `${selectedOperation.method} ${selectedOperation.path}` : ''}
            onChange={e => selectOperation(e.target.value)}><option value="">—</option>
            {operations.filter(operation => operation.method === form.httpMethod.toUpperCase())
              .map(operation => <option value={`${operation.method} ${operation.path}`}
                key={`${operation.method}-${operation.path}`}>
              {operation.method} {operation.path} — {operation.summary}</option>)}</select></label>
          {selectedOperation && <div className="contract-operation-summary">
            <strong>{selectedOperation.fields.length}</strong>
            <span>{t('integrator.externalFieldsFound')}</span>
            <code>{selectedOperation.responseRootPath || t('integrator.responseRoot')}</code>
          </div>}
        </section>}

        {step === 4 && <section className="wizard-panel">
          <h3>{t('integrator.requestConfiguration')}</h3>
          <div className="contract-operation-summary">
            <span>{t('integrator.effectiveServiceUrl')}</span>
            <code>{form.serviceBaseUrl || '—'}</code>
          </div>
          <p className="form-help">{t('integrator.externalCompanyOptionalHelp')}</p>
          <div className="compact-form-grid">
            <label><span>{t('integrator.externalCompanyField')}</span><input
              value={form.externalCompanyField} onChange={e => setForm({ ...form,
                externalCompanyField: e.target.value })}/></label>
            <label><span>{t('integrator.externalCompanyValue')}</span><input
              value={form.externalCompanyValue} onChange={e => setForm({ ...form,
                externalCompanyValue: e.target.value })}/></label>
          </div>
          {usesCompositeKeys(form.operationPath) && <div className="contract-operation-summary">
            <span>{t('integrator.compositeKeys')}</span>
            <input value={effectiveKeys}
              placeholder="txtempresa_id={company},txtof_id={order.number}"
              onChange={event => updateKeysTemplate(event.target.value)}/>
            <small>{t('integrator.compositeKeysHelp')}</small>
          </div>}
          <div className="section-heading"><h4>{t('integrator.requestValues')}</h4>
            <button type="button" className="secondary-button" onClick={() => setForm({
              ...form, requestValues: [...form.requestValues, { location: 'Query',
                name: '', value: '', valueSource: 'Constant' }] })}>+
              {t('administration.add')}</button></div>
          <p className="form-help">{t('integrator.filterTemplateHelp')}</p>
          {selectedOperation?.fieldsQueryParameter && <div className="contract-operation-summary">
            <span>{t('integrator.automaticFields')}</span>
            <code>{selectedOperation.fieldsQueryParameter}={automaticFields || '—'}</code>
            <small>{t('integrator.automaticFieldsHelp')}</small>
          </div>}
          {form.requestValues.map((item, index) => ({ item, index })).filter(({ item }) =>
            !(item.location === 'Path' && item.name.toLocaleLowerCase() === 'keys'))
            .map(({ item, index }) => <div className="integrator-request-row"
            key={index}><select value={item.location} onChange={e => { const next =
              [...form.requestValues]; next[index] = { ...item, location:
                e.target.value as typeof item.location }; setForm({ ...form,
                  requestValues: next }) }}>{['Query','Header','Path','Body'].map(x =>
                <option key={x}>{x}</option>)}</select><input placeholder={t('integrator.parameterName')}
              value={item.name} onChange={e => { const next = [...form.requestValues];
                next[index] = { ...item, name: e.target.value }; setForm({ ...form,
                  requestValues: next }) }} onBlur={() => {
                    if (item.location === 'Query' &&
                        item.name.toLocaleLowerCase() === 'fields')
                      setForm(current => ({ ...current, requestValues:
                        current.requestValues.filter((_, row) => row !== index) }))
                  }}/><select value={item.valueSource}
              onChange={e => { const next = [...form.requestValues]; next[index] =
                { ...item, valueSource: e.target.value as typeof item.valueSource };
                setForm({ ...form, requestValues: next }) }}>
              {['Constant','Company','LastSuccessfulRunUtc','CurrentUtc','RecordField']
                .map(x => <option key={x} value={x}>{t(`integrator.valueSource.${x}`)}</option>)}</select>
              <input placeholder={t('integrator.parameterValue')} value={item.value}
                onChange={e => { const next = [...form.requestValues]; next[index] =
                  { ...item, value: e.target.value }; setForm({ ...form,
                    requestValues: next }) }}/><button type="button" className="icon-button"
                onClick={() => setForm({ ...form, requestValues:
                  form.requestValues.filter((_, row) => row !== index) })}>×</button></div>)}
          {form.httpMethod === 'Get' && <><h4>{t('integrator.pagination')}</h4>
            <label><span>{t('integrator.responseRoot')}</span><input maxLength={1000}
              value={form.responseRootPath} placeholder="items"
              onChange={e => setForm({ ...form, responseRootPath: e.target.value })}/>
              <small>{t('integrator.responseRootHelp')}</small></label>
            <div className="compact-form-grid"><label><span>{t('integrator.paginationStrategy')}</span>
              <select value={form.paginationStrategy} onChange={e => setForm({ ...form,
                paginationStrategy: e.target.value as IntegrationForm['paginationStrategy'] })}>
                {['None','PageNumber','Offset','OData','Cursor','NextLink','Keyset'].map(x =>
                  <option key={x} value={x}>{t(`integrator.pagination.${x}`)}</option>)}</select></label>
              <label><span>{t('integrator.pageSize')}</span><input type="number" min="1"
                max="10000" value={form.pageSize} onChange={e => setForm({ ...form,
                  pageSize: Number(e.target.value) })}/></label>
              <label><span>{t('integrator.maximumPages')}</span><input type="number" min="1"
                max="100000" value={form.maximumPages} onChange={e => setForm({ ...form,
                  maximumPages: Number(e.target.value) })}/></label>
              <label><span>{t('integrator.pageTimeout')}</span><input type="number" min="1"
                max="600" value={form.pageTimeoutSeconds} onChange={e => setForm({ ...form,
                  pageTimeoutSeconds: Number(e.target.value) })}/><small>{t('integrator.pageTimeoutHelp')}</small></label>
              {form.paginationStrategy === 'PageNumber' && <><label><span>{t('integrator.pageParameter')}</span><input value={form.pageParameter} onChange={e => setForm({ ...form, pageParameter: e.target.value })}/></label><label><span>{t('integrator.firstPage')}</span><input type="number" min="0" value={form.firstPage} onChange={e => setForm({ ...form, firstPage: Number(e.target.value) })}/></label></>}
              {['PageNumber','Offset','Cursor','Keyset'].includes(form.paginationStrategy) && <label><span>{t('integrator.pageSizeParameter')}</span><input value={form.pageSizeParameter} onChange={e => setForm({ ...form, pageSizeParameter: e.target.value })}/></label>}
              {form.paginationStrategy === 'Offset' && <label><span>{t('integrator.offsetParameter')}</span><input value={form.offsetParameter} onChange={e => setForm({ ...form, offsetParameter: e.target.value })}/></label>}
              {form.paginationStrategy === 'Cursor' && <><label><span>{t('integrator.cursorParameter')}</span><input value={form.cursorParameter} onChange={e => setForm({ ...form, cursorParameter: e.target.value })}/></label><label><span>{t('integrator.nextCursorPath')}</span><input value={form.nextCursorPath} onChange={e => setForm({ ...form, nextCursorPath: e.target.value })}/></label></>}
              {form.paginationStrategy === 'NextLink' && <label><span>{t('integrator.nextLinkPath')}</span><input value={form.nextLinkPath} onChange={e => setForm({ ...form, nextLinkPath: e.target.value })}/></label>}
              {form.paginationStrategy === 'Keyset' && <label><span>{t('integrator.keysetField')}</span>
                <select value={form.keysetFieldPath} onChange={e => setForm({ ...form,
                  keysetFieldPath: e.target.value })}><option value="">—</option>
                  {form.keysetFieldPath && !selectedOperation?.fields.some(x =>
                    x.path === form.keysetFieldPath) && <option value={form.keysetFieldPath}>
                    {form.keysetFieldPath}</option>}
                  {selectedOperation?.fields.map(field => <option key={field.path}
                    value={field.path}>{field.path}</option>)}</select>
                <small>{t('integrator.keysetFieldHelp')}</small></label>}
              <label><span>{t('integrator.totalCountPath')}</span><input value={form.totalCountPath} onChange={e => setForm({ ...form, totalCountPath: e.target.value })}/></label>
            </div><p className="form-help">{t('integrator.paginationAllHelp')}</p></>}
          {target?.isGrouped && <div className="integrator-panel-configuration">
            <h4>{t('integrator.groupedPanels')}</h4>
            <p className="form-help">{t('integrator.groupedPanelsHelp')}</p>
            {(target.panels ?? []).map(panel => {
              const configuration = form.panelConfigurations.find(item =>
                item.panelKey === panel.key) ?? { panelKey: panel.key,
                responseRootPath: '', keyFields: [] }
              return <section className="contract-operation-summary grouped-panel-card" key={panel.key}>
                <strong>{t(`integrator.panel.${panel.key}`)}</strong>
                {!panel.isRoot && form.httpMethod === 'Get' && <label className="grouped-panel-root">
                  <span>{t('integrator.panelResponseRoot')}</span>
                  <input value={configuration.responseRootPath} placeholder="lines"
                    onChange={event => setForm(current => ({ ...current,
                      panelConfigurations: current.panelConfigurations.map(item =>
                        item.panelKey === panel.key ? { ...item,
                          responseRootPath: event.target.value } : item),
                    }))}/></label>}
                <span className="grouped-panel-key-label">{t('integrator.panelKeyFields')}</span>
                <div className="integrator-key-fields">{panel.fields.map(field =>
                  <label className="checkbox-row" key={field.key}><input type="checkbox"
                    checked={configuration.keyFields.includes(field.key)}
                    onChange={event => setForm(current => ({ ...current,
                      panelConfigurations: current.panelConfigurations.map(item =>
                        item.panelKey === panel.key ? { ...item, keyFields:
                          event.target.checked ? [...item.keyFields, field.key] :
                            item.keyFields.filter(key => key !== field.key) } : item),
                      keyFields: event.target.checked
                        ? [...new Set([...current.keyFields, `${panel.key}.${field.key}`])]
                        : current.keyFields.filter(key => key !== `${panel.key}.${field.key}`),
                    }))}/>{field.key}</label>)}</div>
              </section>
            })}
          </div>}
          {form.httpMethod === 'Get' && <div className="form-actions">
            <button type="button" className="secondary-button" disabled={busy}
              onClick={() => void runTest(false)}>{t('integrator.testFirstPage')}</button>
            <button type="button" className="secondary-button" disabled={busy}
              onClick={() => void runTest(true)}>{t('integrator.testAllPages')}</button></div>}
          {testResult && <div className={`contract-operation-summary ${
            testResult.succeeded ? '' : 'form-error'}`}><strong>
            {t(testResult.code as Parameters<typeof t>[0])}</strong>
            <span>{t('integrator.testDetailAvailable')}</span></div>}
          {!target?.isGrouped && <><h4>{t('integrator.keyFields')}</h4><div className="integrator-key-fields">
            {target?.fields.map(field => <label className="checkbox-row" key={field.key}>
              <input type="checkbox" checked={form.keyFields.includes(field.key)}
                onChange={e => setForm({ ...form, keyFields: e.target.checked ?
                  [...form.keyFields, field.key] : form.keyFields.filter(x => x !== field.key) })}/>
              {field.key}</label>)}</div></>}
        </section>}

        {step === 5 && <section className="wizard-panel">
          <h3>{t('integrator.step4Title')}</h3>
          <p className="form-help">{t(form.httpMethod === 'Patch'
            ? 'integrator.patchMappingHelp' : isOutboundMapping
              ? 'integrator.outboundMappingHelp' : 'integrator.mappingHelp')}</p>
          {isOutboundMapping && availableOutboundFields.length > 0 &&
            <div className="outbound-field-picker">
              <label><span>{t('integrator.addJsonField')}</span><select
                value={outboundFieldToAdd} onChange={event =>
                  setOutboundFieldToAdd(event.target.value)}>
                <option value="">—</option>
                {availableOutboundFields.map(field => <option value={field.path}
                  key={field.path}>{field.path} ({field.dataType})</option>)}
              </select></label>
              <button type="button" className="secondary-button"
                disabled={!outboundFieldToAdd} onClick={addOutboundField}>
                {t('administration.add')}</button>
            </div>}
          <div className="mapping-table"><div className="mapping-header">
            <span>{t(isOutboundMapping ? 'integrator.sourceField' : 'integrator.targetField')}</span>
            <span>{t(isOutboundMapping ? 'integrator.targetField' : 'integrator.sourceField')}</span>
            <span>{t('integrator.defaultValueType')}</span><span>{t('integrator.defaultValue')}</span>
            <span>{t('integrator.transformation')}</span>
          </div>{mappingRows.map(({ field, external, mapping, rowKey }) => <div
            className="mapping-row" key={rowKey}>
            <label><span>{isOutboundMapping ? external?.path : field?.key}
              {(isOutboundMapping ? isRequiredOutboundField(external?.required ?? false)
                : field?.required) ? ' *' : ''}</span>
              <small>{isOutboundMapping ? external?.dataType : field &&
                `${targetFieldLabel(field.key, t)} · ${field.dataType}`}</small>
              {isOutboundMapping && external &&
                !isRequiredOutboundField(external.required) && <button type="button"
                className="mapping-remove-link" onClick={() =>
                  removeOutboundField(external.path)}>{t('integrator.removeValueMapping')}</button>}
            </label>
            {isOutboundMapping ? <select value={mapping.targetField}
              onChange={e => updateRowMapping(rowKey, { targetField: e.target.value })}>
              <option value="">—</option>
              {target?.fields.map(aplField => <option value={aplField.key} key={aplField.key}
                disabled={form.mappings.some(item => item.sourcePath !== rowKey &&
                  item.targetField === aplField.key)}>{aplField.key} · {
                  targetFieldLabel(aplField.key, t)} ({aplField.dataType})</option>)}
            </select> : <select value={mapping.sourcePath}
              onChange={e => updateRowMapping(rowKey, { sourcePath: e.target.value })}>
              <option value="">—</option>
              {selectedOperation?.fields.map(item => <option value={item.path}
                key={item.path}>{item.path} ({item.dataType})</option>)}</select>}
            <select value={defaultValueKind(mapping.defaultValue)}
              onChange={e => updateRowMapping(rowKey, { defaultValue:
                e.target.value === 'Function' ? `${defaultFunctionPrefix}UtcNow` : '' })}>
              <option value="Fixed">{t('integrator.defaultValueType.Fixed')}</option>
              {outboundFunctionsEnabled(form) && <option value="Function">
                {t('integrator.defaultValueType.Function')}</option>}
            </select>
            {defaultValueKind(mapping.defaultValue) === 'Function' ? <select
              value={defaultFunction(mapping.defaultValue)}
              onChange={e => updateRowMapping(rowKey,
                { defaultValue: `${defaultFunctionPrefix}${e.target.value}` })}>
              {outboundDefaultFunctions.map(value => <option value={value} key={value}>
                {t(`integrator.defaultFunction.${value}`)}</option>)}
            </select> : field?.allowedValues?.length ? <select value={mapping.defaultValue}
              onChange={e => updateRowMapping(rowKey, { defaultValue: e.target.value })}>
              <option value="">—</option>
              {mapping.defaultValue && !field.allowedValues.includes(mapping.defaultValue) &&
                <option value={mapping.defaultValue}>{mapping.defaultValue}</option>}
              {field.allowedValues.map(value => <option value={value} key={value}>
                {allowedValueLabel(field.key, value, t)}</option>)}
            </select> : <input value={mapping.defaultValue} onChange={e => updateRowMapping(rowKey,
              { defaultValue: e.target.value })}/>} {/* catalog-aware default */}
            <select value={transformationKind(mapping.transformation)} onChange={e => updateRowMapping(rowKey,
              { transformation: e.target.value === 'Counter' ? counterTransformation([])
                  : e.target.value === 'IfNull' ? ifNullTransformation('') : e.target.value,
                ...(e.target.value === 'Counter' && form.direction === 'Inbound'
                  ? { sourcePath: '', defaultValue: '' } : {}) })}>{[
                    'None','Trim','Uppercase','Lowercase','BooleanZeroMinusOne',
                    'HoursToMinutes', ...(form.direction === 'Inbound'
                      ? ['LocalToUtc', 'IfNull'] : ['UtcToLocal']),
                    'ValueMap', 'Counter'].map(value =>
                <option value={value} key={value}>{t(`integrator.transform.${value}`)}</option>)}</select>
            {transformationKind(mapping.transformation) === 'IfNull' && <label>
              <span>{t('integrator.fallbackField')}</span><select
                value={ifNullFallbackPath(mapping.transformation)}
                onChange={e => updateRowMapping(rowKey,
                  { transformation: ifNullTransformation(e.target.value) })}>
                <option value="">—</option>
                {selectedOperation?.fields.filter(external => external.path !== mapping.sourcePath)
                  .map(external => <option value={external.path} key={external.path}>
                    {external.path} ({external.dataType})</option>)}
              </select></label>}
            {transformationKind(mapping.transformation) === 'Counter' &&
              <div className="value-mapping-editor counter-group-editor">
                <strong>{t('integrator.counterGroupFields')}</strong>
                <small>{t('integrator.counterGroupFieldsHelp')}</small>
                <div className="integrator-key-fields">{target?.fields
                  .filter(group => group.key !== field?.key)
                  .map(group => {
                    const selected = counterGroupFields(mapping.transformation)
                    return <label className="checkbox-row" key={group.key}>
                      <input type="checkbox" checked={selected.includes(group.key)}
                        onChange={event => updateRowMapping(rowKey, { transformation:
                          counterTransformation(event.target.checked
                            ? [...selected, group.key]
                            : selected.filter(item => item !== group.key)) })}/>
                      {group.key}</label>
                  })}</div>
              </div>}
            {mapping.transformation === 'ValueMap' && <div className="value-mapping-editor">
              <label><span>{t('integrator.unknownValueBehavior')}</span><select
                value={mapping.unknownValueBehavior} onChange={e => updateRowMapping(rowKey,
                  { unknownValueBehavior: e.target.value as 'Reject'|'UseDefault' })}>
                <option value="Reject">{t('integrator.unknownValue.Reject')}</option>
                <option value="UseDefault">{t('integrator.unknownValue.UseDefault')}</option>
              </select></label>
              <div className="value-mapping-heading"><strong>{t('integrator.valueMappings')}</strong>
                <button type="button" className="secondary-button" onClick={() => updateRowMapping(rowKey,
                  { valueMappings: [...mapping.valueMappings, { externalValue: '', targetValue: '', occurrences: 0 }] })}>
                  {t('integrator.addValueMapping')}</button></div>
              {mapping.valueMappings.length === 0 && <small>{t('integrator.valueMappingsEmpty')}</small>}
              {mapping.valueMappings.map((valueMapping, index) => <div className="value-mapping-row"
                key={valueMapping.id ?? index}>
                <input aria-label={t('integrator.externalValue')} placeholder={t('integrator.externalValue')}
                  value={valueMapping.externalValue} onChange={e => updateRowMapping(rowKey,
                    { valueMappings: mapping.valueMappings.map((item, itemIndex) => itemIndex === index
                      ? { ...item, externalValue: e.target.value } : item) })}/>
                <span>→</span>
                {field?.allowedValues?.length ? <select aria-label={t('integrator.aplValue')}
                  value={valueMapping.targetValue} onChange={e => updateRowMapping(rowKey,
                    { valueMappings: mapping.valueMappings.map((item, itemIndex) => itemIndex === index
                      ? { ...item, targetValue: e.target.value } : item) })}>
                  <option value="">—</option>
                  {valueMapping.targetValue && !field.allowedValues.includes(valueMapping.targetValue) &&
                    <option value={valueMapping.targetValue}>{valueMapping.targetValue}</option>}
                  {field.allowedValues.map(value => <option value={value} key={value}>
                    {allowedValueLabel(field.key, value, t)}</option>)}
                </select> : <input aria-label={t('integrator.aplValue')}
                  placeholder={t('integrator.aplValue')} value={valueMapping.targetValue}
                  onChange={e => updateRowMapping(rowKey,
                    { valueMappings: mapping.valueMappings.map((item, itemIndex) => itemIndex === index
                      ? { ...item, targetValue: e.target.value } : item) })}/>} {/* catalog-aware mapping */}
                {valueMapping.occurrences > 0 && <small>{formatNumber(valueMapping.occurrences)} ×</small>}
                <button type="button" className="secondary-button" title={t('integrator.removeValueMapping')}
                  onClick={() => updateRowMapping(rowKey, { valueMappings:
                    mapping.valueMappings.filter((_, itemIndex) => itemIndex !== index) })}>
                  {t('integrator.removeValueMapping')}</button>
              </div>)}
            </div>}
          </div>)}</div>
        </section>}

        {step === 6 && <section className="wizard-panel">
          <h3>{t('integrator.step5Title')}</h3>
          <label><span>{t('integrator.trigger')}</span><select value={form.trigger}
            onChange={e => setForm({ ...form, trigger:
              e.target.value as IntegrationForm['trigger'],
              isScheduleEnabled: e.target.value === 'Schedule' })}>
            {(form.direction === 'Inbound' ? ['Manual','Schedule'] : ['Manual','Schedule',
              'RecordCreated','RecordChanged','StatusChanged',
              ...(form.targetEntity === 'ProductionOrderAggregate'
                ? ['ProductionOrderPlanned']
                : form.targetEntity === 'ProductionOrder' ||
                  form.targetEntity === 'ProductionOrderOperation'
                ? ['ProductionOrderStarted','ProductionOrderCompleted'] : [])]).map(x =>
                <option value={x} key={x}>{t(`integrator.trigger.${x}`)}</option>)}</select></label>
          <label className="checkbox-row"><input type="checkbox"
            checked={form.isScheduleEnabled} onChange={e => setForm({ ...form,
              isScheduleEnabled: e.target.checked,
              trigger: e.target.checked ? 'Schedule' : 'Manual' })}/><span>{t('integrator.configureSchedule')}</span></label>
          {form.isScheduleEnabled && <div className="compact-form-grid">
            <label><span>{t('integrator.interval')}</span><input type="number" min={1}
              max={10000} required value={form.scheduleInterval}
              onChange={e => setForm({ ...form, scheduleInterval: Number(e.target.value) })}/></label>
            <label><span>{t('integrator.scheduleUnit')}</span><select value={form.scheduleUnit}
              onChange={e => setForm({ ...form, scheduleUnit: e.target.value as IntegrationForm['scheduleUnit'] })}>
              {(['Minutes','Hours','Days'] as const).map(value => <option value={value}
                key={value}>{t(`integrator.unit.${value}`)}</option>)}</select></label>
            <label><span>{t('integrator.timeZone')}</span><input required maxLength={100}
              value={form.timeZoneId} onChange={e => setForm({ ...form, timeZoneId: e.target.value })}/></label>
          </div>}
          {!target?.canImport && <div className="integrator-warning">{t('integrator.executorPending')}</div>}
          {form.id && snapshot.integrations.find(x => x.id === form.id)?.nextRunAtUtc &&
            <p>{t('integrator.nextRun')}: {formatDateTime(snapshot.integrations.find(x => x.id === form.id)!.nextRunAtUtc!)}</p>}
        </section>}

        <div className="form-actions wizard-actions">
          <button className="secondary-button" type="button" onClick={() => step === 1
            ? closeEditor() : setStep(step - 1)}>{step === 1 ? t('administration.cancel') : t('integrator.previous')}</button>
          {step === 1 && <button className="primary-button" type="button" disabled={!form.name}
            onClick={() => setStep(2)}>{t('integrator.next')}</button>}
          {step === 2 && <button className="primary-button" type="button" disabled={busy}
            onClick={() => void discover()}>{busy ? t('integrator.inspecting') : t('integrator.inspect')}</button>}
          {step === 3 && <button className="primary-button" type="button"
            disabled={!selectedOperation} onClick={() => setStep(4)}>{t('integrator.next')}</button>}
          {step === 4 && <button className="primary-button" type="button"
            disabled={form.keyFields.length === 0} onClick={() => setStep(5)}>{t('integrator.next')}</button>}
          {step === 5 && <button className="primary-button" type="button"
            disabled={!outboundMappingsComplete}
            onClick={() => setStep(6)}>{t('integrator.next')}</button>}
          {step === 6 && <button className="primary-button" type="submit" disabled={busy}>
            {busy ? t('integrator.saving') : t('administration.save')}</button>}
        </div>
      </form>
    </AppModal>}
  </main>
}
